/* =========================================================
   <instagram-reels>
   PDP carousel of athlete reels. Autoplay muted when in view,
   tap to unmute (shared mute state across all reels), pause
   off-screen, snap-scroll between cards.
   ========================================================= */
(function () {
	'use strict';

	if (customElements.get('instagram-reels')) return;

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	class InstagramReels extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('.instagram-reels__track');
			this.cards = Array.from(this.querySelectorAll('.instagram-reels__card'));
			this.dots = Array.from(this.querySelectorAll('.instagram-reels__dot'));
			this.prevBtn = this.querySelector('.instagram-reels__nav--prev');
			this.nextBtn = this.querySelector('.instagram-reels__nav--next');

			if (!this.track || this.cards.length === 0) return;

			this.muted = true;
			this.activeIndex = 0;
			// Desktop / mouse devices play on hover; touch devices keep intersection-based autoplay.
			this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

			this._setupVideos();
			this._setupVisibilityObserver();
			this._setupHoverPlayback();
			this._setupScrollSync();
			this._setupControls();
			this._setupMuteToggle();
			this._setupCaptionToggle();

			if (this.prevBtn) this.prevBtn.hidden = false;
			if (this.nextBtn) this.nextBtn.hidden = false;
			this._updateNavState();
		}

		_setupVideos() {
			this.cards.forEach(function (card) {
				var video = card.querySelector('video');
				if (!video) return;
				video.muted = true;
				video.playsInline = true;
				video.loop = true;
				video.controls = false;

				var progressBar = card.querySelector('.instagram-reels__progress-bar');
				if (progressBar) {
					video.addEventListener('timeupdate', function () {
						if (!video.duration) return;
						var pct = (video.currentTime / video.duration) * 100;
						progressBar.style.width = pct + '%';
					});
				}
			});
		}

		_setupVisibilityObserver() {
			var self = this;

			if (!('IntersectionObserver' in window) || prefersReducedMotion) {
				return;
			}

			if (this.hoverCapable) {
				// Desktop: hover drives playback. Observer only pauses cards that scroll
				// fully off-screen mid-play, so we don't leave audio/video running.
				this._observer = new IntersectionObserver(function (entries) {
					entries.forEach(function (entry) {
						if (entry.intersectionRatio < 0.1) {
							var card = entry.target;
							var video = card.querySelector('video');
							var media = card.querySelector('.instagram-reels__media');
							if (video && media) self._pauseCard(video, media);
						}
					});
				}, {
					root: this.track,
					threshold: [0, 0.1]
				});
			} else {
				// Touch / no-hover: autoplay when the card is the dominant one in view.
				this._observer = new IntersectionObserver(function (entries) {
					entries.forEach(function (entry) {
						var card = entry.target;
						var video = card.querySelector('video');
						var media = card.querySelector('.instagram-reels__media');
						if (!video || !media) return;

						if (entry.intersectionRatio >= 0.6) {
							self._playCard(video, media);
						} else {
							self._pauseCard(video, media);
						}
					});
				}, {
					root: this.track,
					threshold: [0, 0.25, 0.6, 0.9]
				});
			}

			this.cards.forEach(function (card) {
				self._observer.observe(card);
			});
		}

		_setupHoverPlayback() {
			if (!this.hoverCapable || prefersReducedMotion) return;
			var self = this;

			this.cards.forEach(function (card) {
				var video = card.querySelector('video');
				var media = card.querySelector('.instagram-reels__media');
				if (!video || !media) return;

				card.addEventListener('mouseenter', function () {
					self._playCard(video, media);
				});

				card.addEventListener('mouseleave', function () {
					self._pauseCard(video, media);
					try { video.currentTime = 0; } catch (e) { /* ignore */ }
					var progressBar = card.querySelector('.instagram-reels__progress-bar');
					if (progressBar) progressBar.style.width = '0%';
				});
			});
		}

		_playCard(video, media) {
			if (!video.src && !video.querySelector('source')) return;
			try {
				video.muted = this.muted;
				var playPromise = video.play();
				if (playPromise && typeof playPromise.catch === 'function') {
					playPromise.catch(function () {
						// Autoplay blocked (likely unmuted) — fall back to muted
						video.muted = true;
						video.play().catch(function () { /* give up silently */ });
					});
				}
				media.setAttribute('data-state', 'playing');
			} catch (e) {
				media.setAttribute('data-state', 'initial');
			}
		}

		_pauseCard(video, media) {
			try {
				video.pause();
			} catch (e) { /* ignore */ }
			media.setAttribute('data-state', 'paused');
		}

		_setupMuteToggle() {
			var self = this;
			var muteButtons = Array.from(this.querySelectorAll('.instagram-reels__mute'));

			muteButtons.forEach(function (btn) {
				btn.addEventListener('click', function (e) {
					e.preventDefault();
					self.muted = !self.muted;
					self._applyMuteState();
				});
			});
		}

		_setupCaptionToggle() {
			var captions = Array.from(this.querySelectorAll('.instagram-reels__caption'));

			captions.forEach(function (caption) {
				/* Smooth bidirectional slide via FLIP-style max-height:
				   pin the current rendered height, force a reflow, then
				   set the target height to either the full content (expand)
				   or the 2-line collapsed height (collapse). Because the
				   target equals the actual visible-height-after, the entire
				   transition duration is spent on visible motion (no dead
				   time at the end). */

				var animating = false;

				var toggle = function (e) {
					if (e) {
						e.preventDefault();
						e.stopPropagation();
					}
					if (animating) return;

					var isExpanded = caption.classList.contains('is-expanded');
					var fromHeight = caption.clientHeight;

					if (isExpanded) {
						// COLLAPSE
						// Pin current height
						caption.style.maxHeight = fromHeight + 'px';
						// Force reflow before setting target
						void caption.offsetHeight;

						// Target = 2 lines worth (matches CSS calc(1.35em * 2))
						var lineHeight = parseFloat(getComputedStyle(caption).lineHeight) || 0;
						var targetHeight = lineHeight * 2;
						caption.style.maxHeight = targetHeight + 'px';

						animating = true;
						var onEnd = function (evt) {
							if (evt.propertyName !== 'max-height') return;
							caption.removeEventListener('transitionend', onEnd);
							// Only now revert display + line-clamp so the
							// shrink animation runs cleanly with text in
							// block flow throughout.
							caption.classList.remove('is-expanded');
							caption.style.maxHeight = '';
							animating = false;
						};
						caption.addEventListener('transitionend', onEnd);

						caption.setAttribute('aria-expanded', 'false');
						caption.setAttribute('aria-label', 'Expand caption');
					} else {
						// EXPAND
						// Pin current (collapsed) height
						caption.style.maxHeight = fromHeight + 'px';
						// Swap to expanded styles (block, no line-clamp)
						caption.classList.add('is-expanded');
						// Force reflow to capture full content height
						void caption.offsetHeight;
						var targetHeight = caption.scrollHeight;
						// Animate to the exact content height
						caption.style.maxHeight = targetHeight + 'px';

						animating = true;
						var onEnd = function (evt) {
							if (evt.propertyName !== 'max-height') return;
							caption.removeEventListener('transitionend', onEnd);
							// Clear inline so future content changes (e.g.,
							// metafield edits) don't get clipped.
							caption.style.maxHeight = '';
							animating = false;
						};
						caption.addEventListener('transitionend', onEnd);

						caption.setAttribute('aria-expanded', 'true');
						caption.setAttribute('aria-label', 'Collapse caption');
					}
				};

				caption.addEventListener('click', toggle);
				caption.addEventListener('keydown', function (e) {
					if (e.key === 'Enter' || e.key === ' ') {
						toggle(e);
					}
				});
			});
		}

		_applyMuteState() {
			var self = this;
			var muteButtons = Array.from(this.querySelectorAll('.instagram-reels__mute'));
			muteButtons.forEach(function (btn) {
				btn.setAttribute('data-muted', self.muted ? 'true' : 'false');
				btn.setAttribute('aria-label', self.muted ? 'Unmute' : 'Mute');
			});

			this.cards.forEach(function (card) {
				var video = card.querySelector('video');
				if (video) video.muted = self.muted;
			});
		}

		_setupScrollSync() {
			var self = this;
			var raf = null;

			this.track.addEventListener('scroll', function () {
				if (raf) return;
				raf = requestAnimationFrame(function () {
					raf = null;
					self._updateCurrentDot();
					self._updateNavState();
				});
			}, { passive: true });
		}

		_updateCurrentDot() {
			if (this.dots.length === 0) return;
			var trackRect = this.track.getBoundingClientRect();
			var centerX = trackRect.left + trackRect.width / 2;
			var closestIdx = 0;
			var closestDist = Infinity;

			this.cards.forEach(function (card, i) {
				var rect = card.getBoundingClientRect();
				var cardCenter = rect.left + rect.width / 2;
				var dist = Math.abs(cardCenter - centerX);
				if (dist < closestDist) {
					closestDist = dist;
					closestIdx = i;
				}
			});

			this.activeIndex = closestIdx;

			this.dots.forEach(function (dot, i) {
				var isCurrent = i === closestIdx;
				dot.classList.toggle('is-current', isCurrent);
				dot.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
			});
		}

		_updateNavState() {
			if (!this.prevBtn || !this.nextBtn) return;
			var maxScroll = this.track.scrollWidth - this.track.clientWidth;
			this.prevBtn.disabled = this.track.scrollLeft <= 2;
			this.nextBtn.disabled = this.track.scrollLeft >= maxScroll - 2;
		}

		_setupControls() {
			var self = this;

			if (this.prevBtn) {
				this.prevBtn.addEventListener('click', function () { self._scrollByCard(-1); });
			}
			if (this.nextBtn) {
				this.nextBtn.addEventListener('click', function () { self._scrollByCard(1); });
			}

			this.dots.forEach(function (dot) {
				dot.addEventListener('click', function () {
					var idx = parseInt(dot.getAttribute('data-index'), 10);
					if (!isNaN(idx) && self.cards[idx]) {
						self.cards[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
					}
				});
			});
		}

		_scrollByCard(direction) {
			if (this.cards.length === 0) return;
			var firstCard = this.cards[0];
			var gap = parseInt(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap, 10) || 16;
			var step = firstCard.offsetWidth + gap;
			this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
		}

		disconnectedCallback() {
			if (this._observer) this._observer.disconnect();
		}
	}

	customElements.define('instagram-reels', InstagramReels);
})();
